import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Api from '../api/api';
import Loader from '../shared/loader';
import stringHelpers from '../../utilities/stringHelpers';
import arrayUtilities from '../../utilities/arrayUtilities';
import { ErrorModal } from '../../utilities/errorModal';
import Modal from 'react-bootstrap/Modal';
import { SiteDisallowList } from './siteDisallowList';
import { FiltersModalTrigger } from '../shared/filtersModalTrigger';
import { AppliedFilterPill, renderTagFilterItemLabel } from '../shared/appliedFilterPill';
import appliedFiltersUtilities from '../../utilities/appliedFiltersUtilities';
import { ClientDisallowPageFiltersModal } from './filters-modals/clientDisallowPageFiltersModal';
import { SearchBarWithDebounce } from '../shared/searchBarWithDebounce';

export const ClientDisallowPage = ({ user, organisationId, token, tags }) => {
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false)
    const [siteDetailsLoading, setSiteDetailsLoading] = useState(false)

    const [sites, setSites] = useState(null);
    const [search, setSearch] = useState(null);
    const [filterEnd, setFilterEnd] = useState(null);
    const [filterStart, setFilterStart] = useState(null);
    const [filterPeriodType, setFilterPeriodType] = useState(0);
    const [selectedTags, setSelectedTags] = useState([]);
    const [multiDeleteList, setMultiDeleteList] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [allowListError, setAllowListError] = useState("");
    const [clients, setClients] = useState(null);
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [showFilterPills, setShowFilterPills] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState(null);

    const clearFilterByLabel = (label) => {
        switch (label) {
            case "Period Type":
                setFilterPeriodType(0)
                break
            case "From":
                setFilterStart(null)
                break
            case "To":
                setFilterEnd(null)
                break
            case "Date Range":
                setFilterStart(null)
                setFilterEnd(null)
                break
            case "Tags":
                setSelectedTags([])
                break
            default:
                break
        }
    }

    const handleFilterPillClose = closedLabel => {
        clearFilterByLabel(closedLabel)
        setAppliedFilters(prevState => {
            const updatedFilters = { ...prevState }
            if (closedLabel === "From" || closedLabel === "To" || closedLabel === "Date Range") {
                updatedFilters.From = null
                updatedFilters.To = null
                updatedFilters["Date Range"] = null
            } else {
                updatedFilters[closedLabel] = null
            }
            return appliedFiltersUtilities.hasRemainingFilters(updatedFilters) ? updatedFilters : null
        })
    }

    const handleFilterPillItemRemove = (label, remainingIds) => {
        setAppliedFilters(prevState => appliedFiltersUtilities.keepMultiSelectionItems(prevState, label, remainingIds))
        removeMultiSelectFilterItemByLabel(label, remainingIds)
    }

    const removeMultiSelectFilterItemByLabel = (label, remainingIds) => {
        switch (label) {
            case "Tags":
                setSelectedTags(remainingIds)
                break
            default:
                break
        }
    }

    const isSiteMatchingSelectedTags = (site, client) => {
        if (arrayUtilities.isNullOrEmpty(selectedTags)) return true
        const siteTagIds = (site.tags ?? []).map(tag => tag.id)
        const clientTagIds = (client.tags ?? []).map(tag => tag.id)
        return [...siteTagIds, ...clientTagIds].some(tagId => selectedTags.includes(tagId))
    }

    const getClients = () => {
        setIsLoading(true)
        Api.getClients(
            data => {
                setClients(data);
                if (!arrayUtilities.isNullOrEmpty(data)) {
                    Api.getSites(
                        data => setSites(data),
                        error => console.log(error),
                        organisationId, token
                    )
                }
                else {
                    setSites([])
                }
                setIsLoading(false)
            },
            error => {
                console.log(error)
                setIsLoading(false)
            },
            organisationId,
            token
        )
    }

    useEffect(() => getClients(), []);

    const handleCheckChange = (value) => {
        multiDeleteList.includes(value)
            ? setMultiDeleteList(prevState => prevState.filter(item => item !== value))
            : setMultiDeleteList(prevState => [...prevState, value]);
    }

    const handleMultiDeleteConfirm = () => {
        setIsLoading(true)
        Api.deleteMultipleDisallowedVrms(
            data => {
                if (data.isError) {
                    setAllowListError(data.errorMessage)
                } else {
                    setMultiDeleteList([]);
                    getClients();
                }
                setIsLoading(false)
            },
            error => {
                setIsLoading(false)
                setAllowListError(error.errorMessage)
            },
            multiDeleteList,
            organisationId,
            token
        )
        setShowDeleteModal(false);
    }

    const hasNoTagMatchingSites = !arrayUtilities.isNullOrEmpty(selectedTags)
        && !arrayUtilities.isNullOrEmpty(clients)
        && !arrayUtilities.isNullOrEmpty(sites)
        && !clients.some(client => sites.some(site => site.clientId === client.id && isSiteMatchingSelectedTags(site, client)))

    return (
        <>
            { isLoading && <Loader /> }
            { sites && <>
                <div className="container-fluid">
                    {
                        !stringHelpers.isNullOrEmpty(allowListError) &&
                        <ErrorModal
                            message={ allowListError }
                            onHide={ () => setAllowListError(null) }
                        />
                    }
                    <div className="row justify-content-center">
                        <div className="col-12">
                            <div className="card mb-3">
                                <div className="card-header">
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center">
                                            <h5 className="mb-0">Disallow List</h5>
                                        </div>
                                        <div className="d-flex align-items-center">
                                            <SearchBarWithDebounce onSearchQueryChange={ query => { setSiteDetailsLoading(true); setSearch(query) } } />
                                            <div className='ml-2'>
                                                <FiltersModalTrigger onClick={ () => setShowFiltersModal(true) } />
                                            </div>
                                            <Link to={{ pathname: `/organisation/${organisationId}/disallowed-vrm`, state: { returnUrl: location.pathname } }} className="btn btn-primary btn-sm ml-2 nowrap"><i className="fas fa-plus mr-1"></i> Add to Disallow List</Link>
                                            <button
                                                disabled={ arrayUtilities.isNullOrEmpty(multiDeleteList) }
                                                onClick={ () => setShowDeleteModal(true) }
                                                className="btn btn-danger btn-sm col-auto ml-1">
                                                <i className="fas fa-trash"></i> Delete
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                {
                                    showFilterPills && appliedFilters &&
                                    <div className="px-3 py-2 border-top d-flex align-items-center flex-wrap">
                                        {
                                            Object.entries(appliedFilters).map(
                                                ([label, value]) => <AppliedFilterPill
                                                    key={ label }
                                                    label={ label }
                                                    value={ value }
                                                    renderItemLabel={ renderTagFilterItemLabel }
                                                    onTextClick={ () => setShowFiltersModal(true) }
                                                    onCloseClick={ handleFilterPillClose }
                                                    onRemoveItem={ handleFilterPillItemRemove }
                                                />
                                            )
                                        }
                                        { appliedFilters && Object.keys(appliedFilters).length > 0 && <button type="button" className="btn btn-none p-0 m-0 fs--1 nowrap" onClick={ () => {
                                            setFilterStart(null)
                                            setFilterEnd(null)
                                            setFilterPeriodType(0)
                                            setSelectedTags([])
                                            setAppliedFilters(null)
                                        } }>Clear all filters</button> }
                                    </div>
                                }
                                <div className="card-body bg-light border-top details-container">
                                    <div className="tickets-table">
                                        <div className="">
                                            {
                                                !arrayUtilities.isNullOrEmpty(clients) && !arrayUtilities.isNullOrEmpty(sites) &&
                                                clients.map(
                                                    client => {
                                                        const sitesByClient = sites.filter(item => item.clientId === client.id && isSiteMatchingSelectedTags(item, client))
                                                        return !arrayUtilities.isNullOrEmpty(sitesByClient) && <>
                                                            <h5 className='mb-0'>{ client.name }</h5>
                                                            <div className='pl-2'>
                                                                {
                                                                    sitesByClient.map(
                                                                        site => <SiteDisallowList user={ user } organisationId={ organisationId } token={ token } multiDeleteList={ multiDeleteList } site={ site } key={ site.id } handleCheckChange={ value => handleCheckChange(value) } siteId={ site.id } siteName={ site.name } search={ search } start={ filterStart } end={ filterEnd } filterPeriodType={ filterPeriodType } loading={ siteDetailsLoading } setLoading={ setSiteDetailsLoading } sites={ sites } setErrorMessage={ setAllowListError } />
                                                                    )
                                                                }
                                                            </div>
                                                        </>
                                                    }
                                                )
                                            }
                                            {
                                                hasNoTagMatchingSites &&
                                                <p className="text-center text-600 my-4">No sites match these filters</p>
                                            }
                                            {
                                                !isLoading && arrayUtilities.isNullOrEmpty(clients) &&
                                                <p className="text-center text-600 my-4">You have not been assigned to any sites. Please contact your administrator.</p>
                                            }
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Modal show={ showDeleteModal } onHide={ () => setShowDeleteModal(false) }>
                    <Modal.Header>
                        <Modal.Title>
                            Delete { multiDeleteList.length } VRM{ multiDeleteList.length > 1 ? "s" : "" }. Are you sure?
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Footer>
                        <button
                            className="btn btn-sm btn-danger mt-1"
                            onClick={ () => setShowDeleteModal(false) }
                        >
                            Cancel
                        </button>
                        <button
                            className="btn btn-sm btn-success mt-1"
                            onClick={ handleMultiDeleteConfirm }
                        >
                            Confirm
                        </button>
                    </Modal.Footer>
                </Modal>
                {
                    showFiltersModal &&
                    <ClientDisallowPageFiltersModal
                        showModal={ showFiltersModal }
                        handleClose={ () => setShowFiltersModal(false) }
                        handleConfirm={ (draftFilters) => {
                            setFilterStart(draftFilters.filterStart)
                            setFilterEnd(draftFilters.filterEnd)
                            setFilterPeriodType(draftFilters.filterPeriodType)
                            setSelectedTags(draftFilters.selectedTags)
                            setAppliedFilters(draftFilters.appliedFilters)
                            setShowFilterPills(true)
                            setShowFiltersModal(false)
                        } }
                        initialFilterStart={ filterStart }
                        initialFilterEnd={ filterEnd }
                        initialFilterPeriodType={ filterPeriodType }
                        initialSelectedTags={ selectedTags }
                        initialAppliedFilters={ appliedFilters }
                        tags={ tags ?? [] }
                        key="client-disallow-page-filters-modal"
                    />
                }
            </> }
        </>
    )
}